// src/context/AdvancedMD/AdvancedMdContext.js
import React, {
  createContext,
  useState,
  useCallback,
} from "react";

import { useValidToken } from "../../hooks/useValidToken";

// 🔍 Lookup
import { matchAdvancedMdPatientAbility } from "../../utils/services/AdvancedMD/advancedMdMatchAbility";

// 🧪 Labs
import { getPatientLabs } from "../../utils/services/AdvancedMD/labs";

// 💊 Meds
import { getPatientMedications } from "../../utils/services/AdvancedMD/medications";

// ⚠️ Alergias
import { getPatientAllergies } from "../../utils/services/AdvancedMD/allergies";

// 📝 Notas clínicas
import { getFollowUpExam20Notes } from "../../utils/services/AdvancedMD/followUpExam20";
import { getAnnualWellness20Notes } from "../../utils/services/AdvancedMD/annualWellness20";
import { getTelehealth20Notes } from "../../utils/services/AdvancedMD/telehealth20";
import { getHospitalDischargeFollowUpNotes } from "../../utils/services/AdvancedMD/hospitalDischargeFollowUp";

export const AdvancedMdContext = createContext(null);

export const AdvancedMdProvider = ({ children }) => {
  const { isValid, authLoading } = useValidToken();

  // 🔍 Paciente
  const [patientLookupResults, setPatientLookupResults] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // 🧪 Labs
  const [labs, setLabs] = useState([]);
  const [labsLoading, setLabsLoading] = useState(false);
  const [labsError, setLabsError] = useState(null);

  // 💊 Medications
  const [medications, setMedications] = useState([]);
  const [medicationsLoading, setMedicationsLoading] = useState(false);
  const [medicationsError, setMedicationsError] = useState(null);

  // ⚠️ Allergies
  const [allergies, setAllergies] = useState([]);
  const [allergiesLoading, setAllergiesLoading] = useState(false);
  const [allergiesError, setAllergiesError] = useState(null);

  // 📝 Notas clínicas
  const [followUpExamNotes, setFollowUpExamNotes] = useState([]);
  const [followUpExamLoading, setFollowUpExamLoading] = useState(false);
  const [followUpExamError, setFollowUpExamError] = useState(null);

  const [annualWellnessNotes, setAnnualWellnessNotes] = useState([]);
  const [annualWellnessLoading, setAnnualWellnessLoading] = useState(false);
  const [annualWellnessError, setAnnualWellnessError] = useState(null);

  const [telehealthNotes, setTelehealthNotes] = useState([]);
  const [telehealthLoading, setTelehealthLoading] = useState(false);
  const [telehealthError, setTelehealthError] = useState(null);

  const [hospitalDischargeNotes, setHospitalDischargeNotes] = useState([]);
  const [hospitalDischargeLoading, setHospitalDischargeLoading] =
    useState(false);
  const [hospitalDischargeError, setHospitalDischargeError] = useState(null);

  // ========= 🔍 MATCH PATIENTS =========

  /**
   * Match de paciente en AdvancedMD usando el endpoint /api/advancedmd/match/ability
   *
   * params:
   *  - firstName: string
   *  - lastName: string
   *  - dob: string (ISO-8601)
   *  - sex: string ("M" | "F" | etc.)
   */
  const matchPatientInAdvancedMd = useCallback(
    async ({ firstName, lastName, dob, sex }) => {
      if (!firstName && !lastName) {
        console.warn("matchPatientInAdvancedMd: falta firstName/lastName");
        return;
      }

      setLookupLoading(true);
      setLookupError(null);

      try {
        const payload = {
          firstName: firstName || "",
          lastName: lastName || "",
          dob: dob || null, // o "" según como lo espere el backend
          sex: sex || "",
        };

        const data = await matchAdvancedMdPatientAbility(payload);

        // defensivo: soportar varios formatos de respuesta
        let matches = [];
        let bestMatch = null;

        if (Array.isArray(data)) {
          matches = data;
        } else if (data?.matches) {
          matches = data.matches;
          bestMatch =
            data.bestMatch ||
            (data.matches.length > 0 ? data.matches[0] : null);
        } else if (data) {
          // si el backend devuelve un solo objeto
          matches = [data];
          bestMatch = data;
        }

        setPatientLookupResults(matches);
        if (bestMatch) {
          setSelectedPatient(bestMatch);
        }
      } catch (err) {
        setLookupError(err);
        setPatientLookupResults([]);
      } finally {
        setLookupLoading(false);
      }
    },
    [] // no depende de nada externo, los setters son estables
  );

  // Helper para saber qué patientId usar - wrapped in useCallback to avoid stale closure
  const resolvePatientId = useCallback(
    (patientId) => patientId ?? selectedPatient?.id ?? selectedPatient?.Id ?? null,
    [selectedPatient]
  );

  // ========= 🧪 LABS =========

  const loadLabs = useCallback(
    async (patientId) => {
      const pid = resolvePatientId(patientId);
      if (!pid || authLoading || !isValid) return;

      setLabsLoading(true);
      setLabsError(null);

      try {
        const data = await getPatientLabs(pid);
        setLabs(data || []);
      } catch (err) {
        console.error("AdvancedMD loadLabs error:", err);
        setLabsError(err);
      } finally {
        setLabsLoading(false);
      }
    },
    [authLoading, isValid, resolvePatientId]
  );

  // ========= 💊 MEDICATIONS =========

  const loadMedications = useCallback(
    async (patientId) => {
      const pid = resolvePatientId(patientId);
      if (!pid || authLoading || !isValid) return;

      setMedicationsLoading(true);
      setMedicationsError(null);

      try {
        const data = await getPatientMedications(pid);
        setMedications(data || []);
      } catch (err) {
        console.error("AdvancedMD loadMedications error:", err);
        setMedicationsError(err);
      } finally {
        setMedicationsLoading(false);
      }
    },
    [authLoading, isValid, resolvePatientId]
  );

  // ========= ⚠️ ALLERGIES =========

  const loadAllergies = useCallback(
    async (patientId) => {
      const pid = resolvePatientId(patientId);
      if (!pid || authLoading || !isValid) return;

      setAllergiesLoading(true);
      setAllergiesError(null);

      try {
        const data = await getPatientAllergies(pid);
        setAllergies(data || []);
      } catch (err) {
        console.error("AdvancedMD loadAllergies error:", err);
        setAllergiesError(err);
      } finally {
        setAllergiesLoading(false);
      }
    },
    [authLoading, isValid, resolvePatientId]
  );

  // ========= 📝 CLINICAL NOTES =========

  const loadFollowUpExamNotes = useCallback(
    async (patientId) => {
      const pid = resolvePatientId(patientId);
      if (!pid || authLoading || !isValid) return;

      setFollowUpExamLoading(true);
      setFollowUpExamError(null);

      try {
        const data = await getFollowUpExam20Notes(pid);
        setFollowUpExamNotes(data || []);
      } catch (err) {
        console.error("AdvancedMD loadFollowUpExamNotes error:", err);
        setFollowUpExamError(err);
      } finally {
        setFollowUpExamLoading(false);
      }
    },
    [authLoading, isValid, resolvePatientId]
  );

  const loadAnnualWellnessNotes = useCallback(
    async (patientId) => {
      const pid = resolvePatientId(patientId);
      if (!pid || authLoading || !isValid) return;

      setAnnualWellnessLoading(true);
      setAnnualWellnessError(null);

      try {
        const data = await getAnnualWellness20Notes(pid);
        setAnnualWellnessNotes(data || []);
      } catch (err) {
        console.error("AdvancedMD loadAnnualWellnessNotes error:", err);
        setAnnualWellnessError(err);
      } finally {
        setAnnualWellnessLoading(false);
      }
    },
    [authLoading, isValid, resolvePatientId]
  );

  const loadTelehealthNotes = useCallback(
    async (patientId) => {
      const pid = resolvePatientId(patientId);
      if (!pid || authLoading || !isValid) return;

      setTelehealthLoading(true);
      setTelehealthError(null);

      try {
        const data = await getTelehealth20Notes(pid);
        setTelehealthNotes(data || []);
      } catch (err) {
        console.error("AdvancedMD loadTelehealthNotes error:", err);
        setTelehealthError(err);
      } finally {
        setTelehealthLoading(false);
      }
    },
    [authLoading, isValid, resolvePatientId]
  );

  const loadHospitalDischargeNotes = useCallback(
    async (patientId) => {
      const pid = resolvePatientId(patientId);
      if (!pid || authLoading || !isValid) return;

      setHospitalDischargeLoading(true);
      setHospitalDischargeError(null);

      try {
        const data = await getHospitalDischargeFollowUpNotes(pid);
        setHospitalDischargeNotes(data || []);
      } catch (err) {
        console.error("AdvancedMD loadHospitalDischargeNotes error:", err);
        setHospitalDischargeError(err);
      } finally {
        setHospitalDischargeLoading(false);
      }
    },
    [authLoading, isValid, resolvePatientId]
  );

  return (
    <AdvancedMdContext.Provider
      value={{
        // Auth state
        authLoading,
        isValid,

        // 🔍 match
        patientLookupResults,
        lookupLoading,
        lookupError,
        selectedPatient,
        setSelectedPatient,
        matchPatientInAdvancedMd,

        // 🧪 Labs
        labs,
        labsLoading,
        labsError,
        loadLabs,

        // 💊 Medications
        medications,
        medicationsLoading,
        medicationsError,
        loadMedications,

        // ⚠️ Allergies
        allergies,
        allergiesLoading,
        allergiesError,
        loadAllergies,

        // 📝 Notes
        followUpExamNotes,
        followUpExamLoading,
        followUpExamError,
        loadFollowUpExamNotes,

        annualWellnessNotes,
        annualWellnessLoading,
        annualWellnessError,
        loadAnnualWellnessNotes,

        telehealthNotes,
        telehealthLoading,
        telehealthError,
        loadTelehealthNotes,

        hospitalDischargeNotes,
        hospitalDischargeLoading,
        hospitalDischargeError,
        loadHospitalDischargeNotes,
      }}
    >
      {children}
    </AdvancedMdContext.Provider>
  );
};
